/**
 * EnergyMonthlyTable Component
 *
 * Table showing monthly energy data with costs breakdown and totals row.
 */

import { Card, CardHeader, CardTitle, CardContent } from '@/components/ui/card'
import { Skeleton } from '@/components/ui/skeleton'

function formatGbp(pence, vatRate = 0.20) {
  const pounds = (pence / 100) * (1 + vatRate)
  return new Intl.NumberFormat('en-GB', {
    style: 'currency', currency: 'GBP',
    minimumFractionDigits: 2, maximumFractionDigits: 2
  }).format(pounds)
}

function formatGbpExcl(pence) {
  return new Intl.NumberFormat('en-GB', {
    style: 'currency', currency: 'GBP',
    minimumFractionDigits: 2, maximumFractionDigits: 2
  }).format(pence / 100)
}

function formatMonth(monthStr) {
  const date = new Date(monthStr)
  return date.toLocaleDateString('en-GB', { month: 'short', year: 'numeric' })
}

function formatNumber(value, decimals = 0) {
  return new Intl.NumberFormat('en-GB', {
    minimumFractionDigits: decimals,
    maximumFractionDigits: decimals
  }).format(value)
}

export default function EnergyMonthlyTable({ monthly, vatRate = 0.20, isLoading }) {
  if (isLoading) {
    return (
      <Card>
        <CardHeader>
          <CardTitle>Monthly Breakdown</CardTitle>
        </CardHeader>
        <CardContent>
          <div className="space-y-3">
            {[...Array(6)].map((_, i) => (
              <Skeleton key={i} className="h-8 w-full" />
            ))}
          </div>
        </CardContent>
      </Card>
    )
  }

  if (!monthly?.length) {
    return (
      <Card>
        <CardHeader>
          <CardTitle>Monthly Breakdown</CardTitle>
        </CardHeader>
        <CardContent>
          <div className="text-center py-12 text-neutral-500">
            No data available
          </div>
        </CardContent>
      </Card>
    )
  }

  // Reverse for newest first
  const sorted = [...monthly].reverse()

  // Calculate totals
  const totals = monthly.reduce((acc, m) => ({
    kwh: acc.kwh + parseFloat(m.total_kwh),
    energy: acc.energy + parseFloat(m.cost_excl_vat_pence),
    standing: acc.standing + parseFloat(m.standing_excl_vat_pence),
    total: acc.total + parseFloat(m.total_excl_vat_pence)
  }), { kwh: 0, energy: 0, standing: 0, total: 0 })

  return (
    <Card>
      <CardHeader>
        <CardTitle>Monthly Breakdown</CardTitle>
      </CardHeader>
      <CardContent>
        <div className="overflow-x-auto">
          <table className="w-full text-sm">
            <thead>
              <tr className="border-b border-neutral-200">
                <th className="text-left py-3 px-2 font-medium text-neutral-600">Month</th>
                <th className="text-right py-3 px-2 font-medium text-neutral-600">kWh</th>
                <th className="text-right py-3 px-2 font-medium text-neutral-600">Energy</th>
                <th className="text-right py-3 px-2 font-medium text-neutral-600">Standing</th>
                <th className="text-right py-3 px-2 font-medium text-neutral-600">Total (inc VAT)</th>
                <th className="text-right py-3 px-2 font-medium text-neutral-600">Days</th>
              </tr>
            </thead>
            <tbody>
              {sorted.map((m) => {
                const totalInclVat = parseFloat(m.total_excl_vat_pence) / 100 * (1 + vatRate)
                return (
                  <tr key={m.month} className="border-b border-neutral-100 hover:bg-neutral-50">
                    <td className="py-2.5 px-2 font-medium text-neutral-900">{formatMonth(m.month)}</td>
                    <td className="py-2.5 px-2 text-right text-neutral-700">{formatNumber(parseFloat(m.total_kwh), 1)}</td>
                    <td className="py-2.5 px-2 text-right text-neutral-700">{formatGbpExcl(parseFloat(m.cost_excl_vat_pence))}</td>
                    <td className="py-2.5 px-2 text-right text-neutral-700">{formatGbpExcl(parseFloat(m.standing_excl_vat_pence))}</td>
                    <td className="py-2.5 px-2 text-right font-medium text-neutral-900">
                      {new Intl.NumberFormat('en-GB', {
                        style: 'currency', currency: 'GBP',
                        minimumFractionDigits: 2, maximumFractionDigits: 2
                      }).format(totalInclVat)}
                    </td>
                    <td className="py-2.5 px-2 text-right text-neutral-500">{m.days_with_data}</td>
                  </tr>
                )
              })}
            </tbody>
            <tfoot>
              <tr className="border-t-2 border-neutral-300">
                <td className="py-3 px-2 font-bold text-neutral-900">Total</td>
                <td className="py-3 px-2 text-right font-bold text-neutral-900">{formatNumber(totals.kwh, 1)}</td>
                <td className="py-3 px-2 text-right font-bold text-neutral-900">{formatGbpExcl(totals.energy)}</td>
                <td className="py-3 px-2 text-right font-bold text-neutral-900">{formatGbpExcl(totals.standing)}</td>
                <td className="py-3 px-2 text-right font-bold text-neutral-900">
                  {new Intl.NumberFormat('en-GB', {
                    style: 'currency', currency: 'GBP',
                    minimumFractionDigits: 2, maximumFractionDigits: 2
                  }).format(totals.total / 100 * (1 + vatRate))}
                </td>
                <td className="py-3 px-2"></td>
              </tr>
            </tfoot>
          </table>
        </div>
      </CardContent>
    </Card>
  )
}
